import React from "react";
import { Divider, Flex, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useMembership from "@/shared/hooks/useMembership";
import { PathPickHotDealLineSheet } from "@/features/pathpick/types";
import { getDdayFromDateString } from "@/features/pathpick/utils";

interface Props {
  item: PathPickHotDealLineSheet;
  isSelected: boolean;
  onSelect: () => void;
}

function PathPickSearchProductsPopularLineSheetCard({
  item,
  isSelected,
  onSelect,
}: Props) {
  const { t } = useI18nHelper();
  const { membership, isMembershipLoading } = useMembership();

  const dday = getDdayFromDateString(item.orderDeadline);
  const isClosed = dday !== null && dday < 0;
  // 마감 임박(D-3 이하) 여부. 임박이면 빨간색, 여유(D-4 이상)면 파란색 테마.
  const isUrgent = dday !== null && dday >= 0 && dday <= 3;
  // 핫딜 권한이 없는 멤버십에서는 핫딜 라인시트를 잠금(블러 + 안내 오버레이) 처리한다.
  const allowHotDeal = membership?.allowHotdeal ?? false;
  // 로딩 중에는 잠금 오버레이를 띄우지 않아 권한 보유 유저에게 깜빡임이 보이지 않게 한다.
  // 단, 선택(클릭)은 권한이 확인될 때까지 차단한다(기본 거부).
  const isLocked = !isMembershipLoading && !allowHotDeal;

  const deadlineTheme = isClosed
    ? {
        background: "#EFEFED",
        borderColor: "#D4D4D1",
        chipBackground: "#8F8F8C",
      }
    : isUrgent
    ? {
        background: "#FFEEEE",
        borderColor: "#EE7D7A",
        chipBackground: "#EE7D7A",
      }
    : {
        background: "#F0F7FF",
        borderColor: "#A8CEFA",
        chipBackground: "#1272EF",
      };

  const deadlineChipLabel = isClosed
    ? t("path_pick:search_products.popular.deadline_closed")
    : t("path_pick:search_products.popular.deadline_end_dday", {
        dday: dday === 0 ? "D-Day" : `D-${dday}`,
      });

  return (
    <Flex
      position={"relative"}
      padding={"12px"}
      border={isSelected ? "1px solid #1272EF" : "0.5px solid #D4D4D1"}
      borderRadius={"12px"}
      background={"#FFFFFF"}
      cursor={allowHotDeal ? "pointer" : "default"}
      onClick={allowHotDeal ? onSelect : undefined}
      _hover={
        allowHotDeal
          ? { boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.10)" }
          : undefined
      }
    >
      <Flex
        flexDirection={"column"}
        gap={"8px"}
        width={"100%"}
        filter={isLocked ? "blur(4px)" : undefined}
        pointerEvents={isLocked ? "none" : undefined}
        userSelect={isLocked ? "none" : undefined}
      >
        <Flex
          justifyContent={"space-between"}
          alignItems={"center"}
          gap={"8px"}
        >
          <Flex gap={"8px"} alignItems={"center"} overflow={"hidden"}>
            <Text
              textStyle={"caption-bold"}
              color={"#8F8F8C"}
              padding={"1px 8px"}
              borderRadius={"6px"}
              border={"0.5px solid #D4D4D1"}
              flexShrink={0}
            >
              {item.season}
            </Text>
            <Text textStyle={"body-sm-medium"} color={"#8F8F8C"} noOfLines={1}>
              {item.boutique}
            </Text>
          </Flex>
          <AppButton
            variant={isSelected ? "primary" : "white"}
            onClick={onSelect}
          >
            <Text textStyle={"body-sm-bold"}>
              {isSelected
                ? t("path_pick:search_products.popular.viewing")
                : t("path_pick:search_products.popular.view_products")}
            </Text>
          </AppButton>
        </Flex>

        <Text textStyle={"body-lg-bold"} color={"#1D1D1B"} noOfLines={1}>
          {item.name}
        </Text>

        <Flex gap={"6px"} flexWrap={"wrap"}>
          {item.brands.map((brand) => (
            <Text
              key={brand}
              textStyle={"body-sm-medium"}
              color={"#444440"}
              padding={"2px 10px"}
              borderRadius={"8px"}
              border={"0.5px solid #D4D4D1"}
            >
              {brand}
            </Text>
          ))}
        </Flex>

        <Divider borderColor={"#D4D4D1"} />

        <Flex
          justifyContent={"space-between"}
          alignItems={"flex-end"}
          gap={"8px"}
        >
          <Flex flexDirection={"column"} gap={"2px"}>
            <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
              {t("path_pick:search_products.popular.product_count")}
            </Text>
            <Text textStyle={"body-md-bold"} color={"#1D1D1B"}>
              {t("path_pick:search_products.popular.product_count_value", {
                count: item.productCount,
              })}
            </Text>
          </Flex>
          <Flex flexDirection={"column"} gap={"4px"} alignItems={"flex-end"}>
            <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
              {t("path_pick:search_products.popular.published_at_value", {
                date: item.publishedOn,
              })}
            </Text>
            {dday === null ? (
              <Text textStyle={"body-md-bold"} color={"#1D1D1B"}>
                {item.orderDeadline}
              </Text>
            ) : (
              <Flex
                flexDirection={"column"}
                gap={"2px"}
                padding={"6px 10px"}
                borderRadius={"8px"}
                border={`0.5px solid ${deadlineTheme.borderColor}`}
                background={deadlineTheme.background}
              >
                <Flex gap={"6px"} alignItems={"center"}>
                  <Text textStyle={"caption-bold"} color={"#1D1D1B"}>
                    {t("path_pick:search_products.popular.order_deadline")}
                  </Text>
                  <Text
                    textStyle={"caption-bold"}
                    color={"#FFFFFF"}
                    padding={"1px 6px"}
                    borderRadius={"4px"}
                    background={deadlineTheme.chipBackground}
                  >
                    {deadlineChipLabel}
                  </Text>
                </Flex>
                <Text textStyle={"body-md-bold"} color={"#1D1D1B"}>
                  {item.orderDeadline}
                </Text>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>

      {isLocked && (
        <Flex
          position={"absolute"}
          inset={0}
          alignItems={"center"}
          justifyContent={"center"}
          padding={"12px"}
        >
          <Flex
            flexDirection={"column"}
            gap={"6px"}
            alignItems={"center"}
            textAlign={"center"}
            padding={"16px 20px"}
            border={"0.5px solid #D4D4D1"}
            borderRadius={"8px"}
            background={"#FFFFFF"}
            boxShadow={"0px 2px 8px 0px rgba(0, 0, 0, 0.10)"}
          >
            <Text
              textStyle={"caption-bold"}
              color={"#1272EF"}
              padding={"1px 8px"}
              borderRadius={"6px"}
              border={"0.5px solid #A8CEFA"}
              background={"#F0F7FF"}
            >
              {t("path_pick:search_products.popular.hot_deal_locked_badge")}
            </Text>
            <Text textStyle={"body-md-bold"} color={"#1D1D1B"}>
              {t("path_pick:search_products.popular.hot_deal_locked_title")}
            </Text>
            <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
              {t(
                "path_pick:search_products.popular.hot_deal_locked_description"
              )}
            </Text>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
}

export default PathPickSearchProductsPopularLineSheetCard;
